// NavigationBar.js - Fixed for React Router v6 and auth handling
import React, { useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { Link, useLocation } from 'react-router-dom'
import { injectIntl } from 'react-intl'
import AppStore from '../../stores/AppStore'
import AppDispatcher from '../../dispatchers/AppDispatcher'
import PopUp from './Notifications/PopUp'
import { SelectInput } from './Inputs/Inputs'
import { SpeedometerOn } from './Graphics/Speedometer'
import { WindMachine } from './Graphics/WindMachine'
import { Pump } from './Graphics/Pump'
import { Valve } from './Graphics/Valve'
import { switchCustomer } from '../../actions/actions'
import styles from '../../../css/settings.module.css'
import {
  activeSubMenuIcon,
  clearSpace,
  clearSpaceHideSub,
  clearSpaceFilter,
  dropDown,
  dropDownActive,
  envBadge,
  fixedBar,
  leftNav,
  links,
  logo,
  mobileHidden,
  navBar,
  navBarStage,
  navBarDev,
  rightNav,
  subHeader,
  subHeaderCenter,
  subHeaderLeft,
  subHeaderRight,
  subHeaderTitle,
  subMenu,
  topOfPage,
  lowerSubHeader,
  mainSubHeader,
} from '../../../css/navigation.module.css'

// Create a custom hook for navigation active state
const useNavigationActive = () => {
  const location = useLocation()

  return useCallback((path) => {
    // Check if the current path matches the navigation item path
    return location.pathname.startsWith(path)
  }, [location.pathname])
}

const Banner = ({ banner = '' }) => (
  <div
    style={{
      height: 'inherit',
      width: 'inherit',
      margin: 'auto 0px',
    }}
    dangerouslySetInnerHTML={{ __html: banner }}
  />
)

const NavigationBar = (props) => {
  const {
    backFunc,
    intl: { formatMessage },
    logOut,
    logOutOnly = false,
    menuItems = [],
    name,
    rightControl,
    filterMenuIsActive = false,
    subMenuClose,
  } = props

  const isActive = useNavigationActive()
  const nodeRef = useRef(null)

  // State management with useState
  const [dropDownIsActive, setDropDownIsActive] = useState(false)
  const [subMenuIsActive, setSubMenuIsActive] = useState(false)
  const [switchInitiated, setSwitchInitiated] = useState(AppStore.data.customer.changing)
  const [bannerSVG, setBannerSVG] = useState(() => {
    const { banner_svg: bannerSVG = undefined } = AppStore.getCustomerBrand() || {}
    return bannerSVG
  })

  // AppStore change listener
  const appStoreChangeListener = useCallback(() => {
    const { banner_svg: bannerSVG = undefined } = AppStore.getCustomerBrand() || {}
    setBannerSVG(bannerSVG)
    if (!AppStore.data.customer.changing) {
      setSwitchInitiated(false)
    }
  }, [])

  // Event handlers with useCallback for performance
  const closeDropdown = useCallback(() => {
    setDropDownIsActive(false)
  }, [])

  const openDropdown = useCallback(() => {
    setDropDownIsActive(true)
  }, [])

  const handleClick = useCallback((e) => {
    if (nodeRef.current && nodeRef.current.contains(e.target)) {
      return
    }
    closeDropdown()
  }, [closeDropdown])

  const handleCustomerSelection = useCallback((event) => {
    const {
      target: { selectedOptions, value: customerID },
    } = event

    const selectedCompany = selectedOptions[0].text

    setDropDownIsActive(false)
    setSwitchInitiated(true)

    const { id: userID } = AppStore.getUser()

    switchCustomer(customerID, userID, selectedCompany, () => {
      // React Router v6 no longer uses context for router access
      window.location.href = '/home'
    })
  }, [])

  const toggleSubMenu = useCallback(() => {
    setSubMenuIsActive(prev => {
      const newState = !prev
      if (newState && subMenuClose) {
        subMenuClose()
      }
      return newState
    })
  }, [subMenuClose])

  // Safe handling of logOut prop
  const safeLogOut = useCallback((e) => {
    e.preventDefault()
    if (typeof logOut === 'function') {
      logOut()
    } else {
      console.warn('logOut prop is not a function')
      // Fallback behavior - redirect to login page
      window.location.href = '/login'
    }
  }, [logOut])

  // Lifecycle effects
  useEffect(() => {
    document.addEventListener('mousedown', handleClick, false)
    AppStore.addChangeListener(appStoreChangeListener)

    return () => {
      AppStore.removeChangeListener(appStoreChangeListener)
      document.removeEventListener('mousedown', handleClick, false)
    }
  }, [handleClick, appStoreChangeListener])

  const { id: customerID } = AppStore.getCustomer() || { id: '' }

  const user = AppStore.getUser() || {}
  const customers = (!!user && user.customers) || []
  const currentCustomer = AppStore.getCustomer() || {}

  const customerOptions = customers
    .filter((customer) => customer.is_active)
    .map(
      (customer) =>
        new Object({
          value: customer.id,
          name: customer.customer_name,
        })
    )
    .sort((a, b) => {
      if (!a.name) return true
      if (!b.name) return false
      else return a.name.localeCompare(b.name)
    })

  // Determine navbar style based on environment
  const appEnv = import.meta.env.VITE_APP_ENV
  let navBarStyle = navBar // default to production style
  if (appEnv === 'stage') {
    navBarStyle = navBarStage
  } else if (appEnv === 'dev') {
    navBarStyle = navBarDev
  }

  // Show environment label for non-production
  const showEnvLabel = appEnv === 'stage' || appEnv === 'dev'
  const envLabel = appEnv === 'stage' ? 'STAGE' : appEnv === 'dev' ? 'DEV' : ''

  const dropdownStyles = dropDownIsActive
    ? `${dropDown} ${dropDownActive}`
    : dropDown
  const hasLogOut = typeof logOut === 'function'
  const iconClass = `fa ${
    dropDownIsActive ? 'fa-times' : 'fa-bars'
  } fa-lg afa-inverse`
  const showSubHeader =
    menuItems.length > 0 || !!name || !!rightControl || !!backFunc

  let wrapperClass = clearSpace
  if (showSubHeader && filterMenuIsActive) {
    wrapperClass = `${clearSpace} ${clearSpaceFilter}`
  } else if (!showSubHeader) {
    wrapperClass = clearSpaceHideSub
  }

  const LOG_OUT_TEXT = formatMessage({
    id: 'navigation.signout',
    defaultMessage: 'Sign Out',
  })
  const PROFILE_TEXT = formatMessage({
    id: 'navigation.profile',
    defaultMessage: 'Profile',
  })
  const SETTINGS_TEXT = formatMessage({
    id: 'navigation.settings',
    defaultMessage: 'Settings',
  })
  const SUPPORT_TEXT = formatMessage({
    id: 'navigation.support',
    defaultMessage: 'Support',
  })
  const DISTRIBUTOR_TEXT = formatMessage({
    id: 'navigation.distributor',
    defaultMessage: 'Distributor Tools',
  })

  // In React Router v6, use a simpler NavLink component
  const NavLink = ({ to, text }) => (
    <li>
      <Link
        className={isActive(to) ? 'active' : ''}
        onClick={closeDropdown}
        to={to}
      >
        {text}
      </Link>
    </li>
  )

  return (
    <div className={`${wrapperClass} ${topOfPage}`}>
      <div className={fixedBar} name='navigationTopBar'>
        <div className={navBarStyle} role='group'>
          <span className={leftNav}>
            <Link to='/home'>
              <div className={logo}>
                <Banner banner={bannerSVG} />
              </div>
            </Link>
            {showEnvLabel && (
              <span className={envBadge}>
                {envLabel}
              </span>
            )}
          </span>
          <div className={rightNav} ref={nodeRef}>
            <h5>
              <i
                className={iconClass}
                style={{ padding: '13px', cursor: 'pointer' }}
                aria-hidden='true'
                onClick={
                  dropDownIsActive ? closeDropdown : openDropdown
                }
              />
            </h5>
            <nav className={links}>
              <ul className={dropdownStyles} name='navigationTopBarDropdown'>
                {Array.isArray(customerOptions) &&
                  customerOptions.length > 1 && (
                    <li id='customer-select' style={{ marginRight: '0px' }}>
                      <div
                        style={{
                          display: 'flex',
                          verticalAlign: 'text-bottom',
                        }}
                      >
                        <SelectInput
                          value={customerID}
                          field='customerID'
                          childChange={handleCustomerSelection}
                          options={customerOptions}
                          style={styles.ruleSelectInput}
                          caret
                        />
                        &nbsp;
                      </div>
                    </li>
                )}
                {!logOutOnly && <NavLink text={PROFILE_TEXT} to='/user' />}
                {!logOutOnly &&
                  ((user.permissions &&
                    user.permissions.level >= 20 &&
                    currentCustomer.is_distributor) ||
                    (user.permissions && user.permissions.level >= 30)) && (
                      <NavLink text={DISTRIBUTOR_TEXT} to='/distributor' />
                )}
                {!logOutOnly && (
                  <NavLink text={SETTINGS_TEXT} to='/settings' />
                )}
                {!logOutOnly && <NavLink text={SUPPORT_TEXT} to='/support' />}
                {hasLogOut && (
                  <li>
                    <a
                      onClick={safeLogOut}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') safeLogOut(e)
                      }}
                      role='button'
                      tabIndex={0}
                      style={{ cursor: 'pointer' }}
                    >
                      {LOG_OUT_TEXT}
                    </a>
                  </li>
                )}
              </ul>
            </nav>
          </div>
        </div>
        <div className={`${subHeader} ${mainSubHeader}`}>
          <div
            className={subHeaderLeft}
            name='navigationBarSubHeaderLeft'
            style={{ cursor: 'pointer' }}
          >
            {backFunc && !subMenuIsActive && (
              <i
                className='fa fa-chevron-left fa-lg'
                aria-hidden='true'
                onClick={backFunc}
              />
            )}
            {subMenuIsActive && (
              <i
                className='fa fa-chevron-left fa-lg'
                aria-hidden='true'
                onClick={toggleSubMenu}
              />
            )}
          </div>
          <div
            className={subHeaderCenter}
            name='navigationBarSubHeaderCenter'
          >
            {name && (
              <h4 className={`${subHeaderTitle} `} name='subHeaderTitle'>
                {name}
              </h4>
            )}
          </div>
          {rightControl && (
            <div
              className={subHeaderRight}
              name='navigationBarSubHeaderRight'
            >
              <div>
                <i
                  className={`fa ${rightControl.icon} fa-lg`}
                  aria-hidden='true'
                  onClick={rightControl.func}
                />
              </div>
            </div>
          )}
          {!rightControl && (
            <div
              className={subHeaderRight}
              name='navigationBarSubHeaderRight'
            >
              {subMenuIsActive && (
                <ul className={subMenu}>
                  {menuItems.map((item, i) => {
                    const { active: isActive, clickFunc, icon } = item
                    const subMenuIconClass = `fa ${icon} fa-lg ${
                      isActive ? activeSubMenuIcon : ''
                    }`
                    const key = `${icon}-${i}`
                    return (
                      <li key={key}>
                        <a
                          onClick={clickFunc}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') clickFunc(e)
                          }}
                          role='button'
                          tabIndex={0}
                          style={{ cursor: 'pointer' }}
                        >
                          <i className={subMenuIconClass} />
                        </a>
                      </li>
                    )
                  })}
                </ul>
              )}
              <PopUp
                text={`${
                  AppStore.data.customer.changingName || 'Switching'
                }...`}
                visible={switchInitiated}
              />
            </div>
          )}
        </div>

        {/* FILTER MENU */}
        {filterMenuIsActive && (
          <div className={`${subHeader} ${lowerSubHeader}`}>
            <div
              className={subHeaderCenter}
              name='navigationBarSubHeaderCenter'
            >
              <ul className={subMenu}>
                {menuItems.map((item, i) => {
                  const { active: isActive = false, clickFunc, icon } = item
                  const subMenuIconClass = `fa ${icon} fa-lg ${
                    isActive ? activeSubMenuIcon : ''
                  }`
                  const key = `${icon}-${i}`
                  const listClass = `icon-${i}`
                  let subMenuIcon
                  if (icon === 'rpm') {
                    subMenuIcon = (
                      <SpeedometerOn
                        color={isActive ? '#284047' : '#c4c4c4'}
                      />
                    )
                  } else if (icon === 'wm') {
                    subMenuIcon = (
                      <WindMachine color={isActive ? '#284047' : '#c4c4c4'} />
                    )
                  } else if (icon === 'pump') {
                    subMenuIcon = (
                      <Pump color={isActive ? '#284047' : '#c4c4c4'} />
                    )
                  } else if (icon === 'valve') {
                    subMenuIcon = (
                      <Valve color={isActive ? '#284047' : '#c4c4c4'} />
                    )
                  } else {
                    subMenuIcon = <i className={subMenuIconClass} />
                  }
                  return (
                    <li key={key} className={listClass}>
                      <a
                        onClick={clickFunc}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') clickFunc(e)
                        }}
                        name={icon}
                        role='button'
                        tabIndex={0}
                        style={{ cursor: 'pointer' }}
                      >
                        {subMenuIcon}
                      </a>
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
        )}
        {/* END FILTER MENU */}
      </div>
    </div>
  )
}

NavigationBar.propTypes = {
  backFunc: PropTypes.func,
  intl: PropTypes.object.isRequired,
  logOut: PropTypes.func,
  logOutOnly: PropTypes.bool,
  menuItems: PropTypes.array,
  name: PropTypes.string,
  rightControl: PropTypes.object,
  filterMenuIsActive: PropTypes.bool,
  subMenuClose: PropTypes.func,
}

// Export with injectIntl
export default injectIntl(NavigationBar)
